import {announce} from '@github-ui/aria-live'
import {reportIpmAction, type IpmMetricTags} from '@github-ui/ipm-telemetry'
/* eslint-disable-next-line primer-react/no-deprecated-flash -- Existing Flash usage should be migrated to Banner. */
import {Button, Flash} from '@primer/react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {IpmBannerBase} from '../ipm-banner-base/IpmBannerBase'

export interface PullRequestCcrAdoptionNudgeAnalytics {
  category: string
  location: string
  ctaAction: string
  organization?: number | string
  plan?: string
}

export interface PullRequestCcrAdoptionNudgeProps {
  title: string
  description: string
  ctaTitle: string
  // Id of the existing PR "Select reviewers" form. The CTA submits that form
  // with Copilot-only fields scoped to that submission, so the request flows
  // through the same already-validated server path without affecting other
  // reviewer changes.
  reviewersFormId: string
  // Copilot reviewer bot user id. When it can't be resolved there is no bot to
  // request, so the nudge renders nothing.
  botUserId?: number | string | null
  dismissPath: string
  errorMessage?: string | null
  analytics: PullRequestCcrAdoptionNudgeAnalytics
  testSelector: string
}

const TEST_ID = 'pull-request-ccr-adoption-nudge'
const DISMISSED_LABEL = 'Nudge dismissed.'

export function PullRequestCcrAdoptionNudge({
  title,
  description,
  ctaTitle,
  reviewersFormId,
  botUserId = null,
  dismissPath,
  errorMessage = null,
  analytics,
  testSelector,
}: PullRequestCcrAdoptionNudgeProps) {
  const [isSubmitting, setIsSubmitting] = useState(false)
  const dismissedFocusRef = useRef<HTMLSpanElement>(null)
  const bannerElementRef = useRef<HTMLDivElement>(null)
  const includeCopilotSuggestionOnNextFormDataRef = useRef(false)
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'PullRequestCcrAdoptionNudge',
      surface: 'sidebar_nudge',
      display_location: analytics.location,
      category: analytics.category,
      nudge_id: testSelector,
      variant: 'default',
    }),
    [analytics.category, analytics.location, testSelector],
  )
  const visibleEvent = useMemo(
    () =>
      JSON.stringify({
        category: analytics.category,
        action: 'visible',
        label: 'text: ',
        ...(analytics.organization !== undefined && {organization: analytics.organization}),
        ...(analytics.plan !== undefined && {plan: analytics.plan}),
      }),
    [analytics.category, analytics.organization, analytics.plan],
  )
  const dismissEvent = useMemo(
    () =>
      JSON.stringify({
        category: analytics.category,
        action: 'dismiss',
        label: null,
        location: analytics.location,
        ...(analytics.organization !== undefined && {organization: analytics.organization}),
        ...(analytics.plan !== undefined && {plan: analytics.plan}),
      }),
    [analytics.category, analytics.location, analytics.organization, analytics.plan],
  )
  const ctaEvent = useMemo(
    () =>
      JSON.stringify({
        category: analytics.category,
        action: analytics.ctaAction,
        label: null,
        location: analytics.location,
        ...(analytics.organization !== undefined && {organization: analytics.organization}),
        ...(analytics.plan !== undefined && {plan: analytics.plan}),
      }),
    [analytics.category, analytics.ctaAction, analytics.location, analytics.organization, analytics.plan],
  )

  const ctaButtonRef = useCallback(
    (button: HTMLButtonElement | null) => {
      const form = button?.form
      if (!form) return

      const handleFormData = (event: FormDataEvent) => {
        if (!includeCopilotSuggestionOnNextFormDataRef.current) return

        includeCopilotSuggestionOnNextFormDataRef.current = false
        event.formData.set('suggested_reviewer_id', String(botUserId))
        setIsSubmitting(true)
      }
      const handleSubmitComplete = () => {
        includeCopilotSuggestionOnNextFormDataRef.current = false
        setIsSubmitting(false)
      }
      form.addEventListener('formdata', handleFormData)
      form.addEventListener('submit:complete', handleSubmitComplete)
      return () => {
        form.removeEventListener('formdata', handleFormData)
        form.removeEventListener('submit:complete', handleSubmitComplete)
      }
    },
    [botUserId],
  )

  const handleBannerRemoved = useCallback(() => {
    if (bannerElementRef.current === null) return
    bannerElementRef.current = null

    // Ref cleanup can run before the removed dismiss button releases focus.
    queueMicrotask(() => {
      if (bannerElementRef.current !== null) return

      const sentinel = dismissedFocusRef.current
      if (!sentinel) return

      const active = document.activeElement
      if (active === null || active === document.body) {
        sentinel.focus()
      }

      announce(DISMISSED_LABEL)
    })
  }, [])

  const bannerRef = useCallback(
    (element: HTMLDivElement | null) => {
      if (element) {
        bannerElementRef.current = element
        return handleBannerRemoved
      }

      handleBannerRemoved()
    },
    [handleBannerRemoved],
  )

  // Announce request errors assertively through the shared @github-ui/aria-live
  // region rather than a component-local role="alert", per repository
  // accessibility guidance (4.1.3 status messages / live-region-announcements).
  // errorMessage and botUserId are server-provided props (not client state), so
  // announcing the server-rendered error on mount -- and only when a bot exists --
  // is the correct use of an effect rather than an event handler.
  useEffect(() => {
    // No bot means the nudge renders nothing (see the early return below), so a
    // hidden nudge must stay silent -- don't announce an error for it.
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (botUserId === null || botUserId === undefined) {
      return
    }
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (errorMessage) {
      announce(errorMessage, {assertive: true})
    }
  }, [botUserId, errorMessage])

  // Without a Copilot reviewer bot there is nothing to request, so render nothing.
  if (botUserId === null || botUserId === undefined) {
    return null
  }

  const descriptionNode = (
    <>
      <span data-test-selector={testSelector} />
      <div className="h5 mb-1" aria-hidden="true" data-test-selector="nudge-title">
        {title}
      </div>
      <p className="text-small color-fg-muted mb-0" data-test-selector="nudge-description">
        {description}
      </p>
      {errorMessage ? (
        <Flash variant="danger" className="mt-2" data-test-selector="nudge-error-message">
          {errorMessage}
        </Flash>
      ) : null}
    </>
  )

  const primaryAction = (
    <Button
      ref={ctaButtonRef}
      type="submit"
      form={reviewersFormId}
      variant="primary"
      size="small"
      loading={isSubmitting}
      disabled={isSubmitting}
      loadingAnnouncement="Requesting Copilot code review"
      data-test-selector="nudge-enable-button"
      data-analytics-event={ctaEvent}
      onClick={() => {
        includeCopilotSuggestionOnNextFormDataRef.current = true
        reportIpmAction('cta_click', telemetryTags)
      }}
    >
      {ctaTitle}
    </Button>
  )

  return (
    <>
      <IpmBannerBase
        ref={bannerRef}
        dismissPath={dismissPath}
        visibleEvent={visibleEvent}
        dismissEvent={dismissEvent}
        testId={TEST_ID}
        title={title}
        variant="info"
        location={analytics.location}
        rounded
        telemetryTags={telemetryTags}
        description={descriptionNode}
        primaryAction={primaryAction}
      />
      <span ref={dismissedFocusRef} tabIndex={-1} data-testid={`${TEST_ID}-dismissed`} className="sr-only" />
    </>
  )
}
